import React, { useEffect, useId, useRef, useState } from 'react';
import { Bell, ChevronDown, LogOut, Menu, PanelLeft, Plus, Search, Settings, Sparkles, X, Zap } from 'lucide-react';
import './tokens.css';
import { REHUB_APPS, rehubAppGroups } from './apps';
import type { ReHubAppConfig, ReHubNavItem, ReHubNotice, ReHubUser } from './types';

export interface ReHubDashboardShellProps {
  appId: string;
  accentColour?: string;
  logoSrc?: string;
  nav: ReHubNavItem[];
  activeNavId: string;
  onNavigate: (id: string) => void;
  user?: ReHubUser | null;
  credits?: number | null;
  onAddCredits?: () => void;
  onLogout?: () => void;
  onClose?: () => void;
  showUpgrade?: boolean;
  onUpgrade?: () => void;
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  notices?: ReHubNotice[];
  onDismissNotice?: (id: string) => void;
  pageTitle: string;
  pageSubtitle?: string;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  onOpenApp?: (app: ReHubAppConfig, event: React.MouseEvent<HTMLAnchorElement>) => void;
}

function initials(name: string): string {
  const letter = name.trim().charAt(0);
  return letter ? letter.toUpperCase() : 'R';
}

export function ReHubDashboardShell({
  appId,
  accentColour,
  logoSrc,
  nav,
  activeNavId,
  onNavigate,
  user,
  credits,
  onAddCredits,
  onLogout,
  onClose,
  showUpgrade = false,
  onUpgrade,
  search,
  onSearchChange,
  searchPlaceholder = 'Search your photos…',
  notices = [],
  onDismissNotice,
  pageTitle,
  pageSubtitle,
  headerActions,
  children,
  onOpenApp,
}: ReHubDashboardShellProps) {
  const app = REHUB_APPS.find((item) => item.appId === appId);
  const accent = accentColour || app?.accentColour || '#f97316';
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [headerAccountOpen, setHeaderAccountOpen] = useState(false);
  const [noticesOpen, setNoticesOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const headerMenuRef = useRef<HTMLDivElement>(null);
  const noticeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(event.target as Node)) setAccountOpen(false);
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target as Node)) setHeaderAccountOpen(false);
      if (noticeRef.current && !noticeRef.current.contains(event.target as Node)) setNoticesOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileOpen(false);
        setAccountOpen(false);
        setHeaderAccountOpen(false);
        setNoticesOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const selectNav = (id: string) => {
    onNavigate(id);
    setMobileOpen(false);
  };

  return (
    <div className="rehub-dash fixed inset-0 z-50 flex text-white" style={{ ['--rehub-accent' as string]: accent }}>
      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[248px] flex-col border-r border-white/10 bg-[#0b101a] transition-transform md:static md:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'md:w-[76px]' : 'md:w-[248px]'}`}
        aria-label="Dashboard"
      >
        <div className={`flex items-center gap-2.5 px-4 py-4 ${collapsed ? 'md:justify-center md:px-2' : ''}`}>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-400 via-fuchsia-500 to-violet-600 text-sm font-bold">R</span>
          <div className={collapsed ? 'md:hidden' : ''}>
            <div className="text-[15px] font-semibold leading-none">ReHub</div>
            <div className="mt-1 text-[10px] text-slate-400">Create. Enhance. Market.</div>
          </div>
          <button type="button" className="ml-auto rounded-lg p-1 text-slate-400 hover:bg-white/5 hover:text-white md:hidden" onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <X size={18} />
          </button>
        </div>
        {logoSrc && !collapsed && (
          <div className="px-4 pb-2">
            <img src={logoSrc} alt={app?.appName || 'Current app'} className="h-8 w-auto max-w-[160px] object-contain object-left" />
          </div>
        )}
        <nav className="px-3" aria-label="Sections">
          <p className={`mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 ${collapsed ? 'md:hidden' : ''}`}>Apps</p>
          <ul className="space-y-1">
            {nav.map((item) => {
              const active = item.id === activeNavId;
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => selectNav(item.id)}
                    aria-current={active ? 'page' : undefined}
                    title={item.label}
                    className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm font-medium transition-colors ${
                      active ? 'text-white shadow-sm' : 'text-slate-400 hover:bg-white/5 hover:text-white'
                    } ${collapsed ? 'md:justify-center' : ''}`}
                    style={active ? { background: accent } : undefined}
                  >
                    <Icon size={18} strokeWidth={1.8} />
                    <span className={collapsed ? 'md:hidden' : ''}>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="mt-4 min-h-0 flex-1 overflow-y-auto px-3 pb-3">
          <p className={`mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 ${collapsed ? 'md:sr-only' : ''}`}>All apps</p>
          {rehubAppGroups().map((group) => (
            <div key={group.group} className="mb-2">
              <p className={`px-2 py-1 text-[10px] font-medium text-slate-500 ${collapsed ? 'md:hidden' : ''}`}>{group.group}</p>
              <ul className="space-y-0.5">
                {group.apps.map((item) => {
                  const Icon = item.icon;
                  const current = item.appId === appId;
                  return (
                    <li key={item.appId}>
                      <a
                        href={item.url}
                        title={item.appName}
                        onClick={(event) => onOpenApp?.(item, event)}
                        className={`flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] ${
                          current ? 'bg-white/10 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'
                        } ${collapsed ? 'md:justify-center' : ''}`}
                        aria-current={current ? 'page' : undefined}
                      >
                        <Icon size={15} strokeWidth={1.8} style={{ color: item.accentColour }} />
                        <span className={`truncate ${collapsed ? 'md:hidden' : ''}`}>{item.appName}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
        {showUpgrade && onUpgrade && (
          <div className={`mx-3 mb-3 rounded-2xl border border-white/10 bg-[#121826] p-3 ${collapsed ? 'md:hidden' : ''}`}>
            <p className="text-sm font-semibold">Upgrade for more powerful tools</p>
            <p className="mt-1 text-xs leading-relaxed text-slate-400">Get more credits and unlock premium features.</p>
            <button
              type="button"
              onClick={onUpgrade}
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-white"
              style={{ background: accent }}
            >
              <Sparkles size={14} /> Upgrade now
            </button>
          </div>
        )}
        {user && (
          <div className="relative border-t border-white/10 p-3" ref={accountRef}>
            <button
              type="button"
              className={`flex w-full items-center gap-2.5 rounded-xl px-1.5 py-1.5 text-left hover:bg-white/5 ${collapsed ? 'md:justify-center' : ''}`}
              aria-expanded={accountOpen}
              aria-haspopup="menu"
              onClick={() => setAccountOpen((open) => !open)}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white" style={{ background: accent }}>
                {initials(user.name)}
              </span>
              <span className={`min-w-0 flex-1 ${collapsed ? 'md:hidden' : ''}`}>
                <span className="block truncate text-sm font-medium">{user.name}</span>
                {user.email && <span className="block truncate text-[11px] text-slate-400">{user.email}</span>}
              </span>
              <ChevronDown size={14} className={`text-slate-500 ${collapsed ? 'md:hidden' : ''}`} />
            </button>
            {accountOpen && (
              <div role="menu" className="absolute bottom-16 left-3 z-20 w-52 rounded-xl border border-white/10 bg-[#161d2c] p-1 shadow-2xl">
                <button type="button" role="menuitem" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/5" onClick={() => { setAccountOpen(false); onNavigate('settings'); }}>
                  <Settings size={15} /> Settings
                </button>
                {onClose && (
                  <button type="button" role="menuitem" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/5" onClick={() => { setAccountOpen(false); onClose(); }}>
                    <X size={15} /> Close dashboard
                  </button>
                )}
                {onLogout && (
                  <button type="button" role="menuitem" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-300 hover:bg-red-500/10" onClick={onLogout}>
                    <LogOut size={15} /> Sign out
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="relative flex h-16 shrink-0 items-center gap-2 border-b border-white/10 px-3 sm:gap-3 sm:px-5">
          <button type="button" className="rounded-lg p-2 text-slate-300 hover:bg-white/5 md:hidden" onClick={() => setMobileOpen(true)} aria-label="Open navigation">
            <Menu size={18} />
          </button>
          <button type="button" className="hidden rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-white md:inline-flex" onClick={() => setCollapsed((value) => !value)} aria-pressed={collapsed} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <PanelLeft size={18} />
          </button>
          {onSearchChange && (
            <label className="relative min-w-0 flex-1 max-w-xl">
              <span className="sr-only">Search</span>
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={search ?? ''}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder={searchPlaceholder}
                className="w-full rounded-xl border border-white/10 bg-[#101622] py-2 pl-9 pr-9 text-sm text-white placeholder:text-slate-500 focus:border-white/20 focus:outline-none"
              />
              {search && (
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 hover:text-white" onClick={() => onSearchChange('')} aria-label="Clear search">
                  <X size={14} />
                </button>
              )}
            </label>
          )}
          <div className="ml-auto flex items-center gap-2">
            {typeof credits === 'number' && (
              <div className="hidden items-center gap-1.5 rounded-full border border-white/10 bg-[#101622] px-3 py-1.5 text-sm font-semibold sm:flex">
                <Zap size={14} className="fill-orange-400 text-orange-400" />
                <span>{credits.toLocaleString()} credits</span>
              </div>
            )}
            {typeof credits === 'number' && (
              <div className="flex items-center gap-1 rounded-full border border-white/10 bg-[#101622] px-2 py-1 text-xs font-semibold sm:hidden">
                <Zap size={12} className="fill-orange-400 text-orange-400" />
                {credits.toLocaleString()}
              </div>
            )}
            {onAddCredits && (
              <button type="button" onClick={onAddCredits} className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-semibold text-white sm:px-3 sm:text-sm" style={{ background: accent }}>
                <Plus size={14} /> <span className="hidden xs:inline sm:inline">Add credits</span>
              </button>
            )}
            <div className="relative" ref={noticeRef}>
              <button type="button" className="relative rounded-xl border border-white/10 p-2 text-slate-300 hover:bg-white/5" aria-label="Notifications" aria-expanded={noticesOpen} onClick={() => setNoticesOpen((open) => !open)}>
                <Bell size={16} />
                {notices.length > 0 && <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-orange-400" />}
              </button>
              {noticesOpen && (
                <div className="absolute right-0 top-11 z-30 w-72 rounded-xl border border-white/10 bg-[#161d2c] p-2 shadow-2xl">
                  <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Notifications</p>
                  {notices.length === 0 ? (
                    <p className="px-2 py-3 text-sm text-slate-400">You’re up to date.</p>
                  ) : (
                    <ul className="max-h-72 space-y-1 overflow-y-auto">
                      {notices.map((notice) => (
                        <li key={notice.id} className="rounded-lg px-2 py-2 hover:bg-white/5">
                          <div className="flex items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium">{notice.title}</p>
                              {notice.body && <p className="text-xs text-slate-400">{notice.body}</p>}
                            </div>
                            {onDismissNotice && (
                              <button type="button" aria-label="Dismiss notification" className="text-slate-500 hover:text-white" onClick={() => onDismissNotice(notice.id)}>
                                <X size={14} />
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="relative" ref={headerMenuRef}>
            {user && (
              <button type="button" className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold" style={{ background: accent }} aria-label="Account menu" aria-expanded={headerAccountOpen} onClick={() => setHeaderAccountOpen((open) => !open)}>
                {initials(user.name)}
              </button>
            )}
            {headerAccountOpen && user && (
              <div role="menu" className="absolute right-0 top-11 z-30 w-52 rounded-xl border border-white/10 bg-[#161d2c] p-1 shadow-2xl">
                <button type="button" role="menuitem" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/5" onClick={() => { setHeaderAccountOpen(false); onNavigate('settings'); }}>
                  <Settings size={15} /> Settings
                </button>
                {onClose && (
                  <button type="button" role="menuitem" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/5" onClick={() => { setHeaderAccountOpen(false); onClose(); }}>
                    <X size={15} /> Close dashboard
                  </button>
                )}
                {onLogout && (
                  <button type="button" role="menuitem" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-300 hover:bg-red-500/10" onClick={onLogout}>
                    <LogOut size={15} /> Sign out
                  </button>
                )}
              </div>
            )}
            </div>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-5 px-4 py-5 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{pageTitle}</h1>
                {pageSubtitle && <p className="mt-1 text-sm text-slate-400">{pageSubtitle}</p>}
              </div>
              {headerActions && <div className="flex flex-wrap items-center gap-2">{headerActions}</div>}
            </div>
            {children}
            <p className="pb-2 text-center text-[11px] text-slate-500">ReHub Studio · AI tools for property marketing · Make every property look its best.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ReHubStat({ icon, value, label, tint }: { icon: React.ReactNode; value: React.ReactNode; label: string; tint: string }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#101622] px-3 py-3">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tint}`}>{icon}</span>
      <span>
        <span className="block text-lg font-semibold leading-none">{value}</span>
        <span className="mt-1 block text-xs text-slate-400">{label}</span>
      </span>
    </div>
  );
}

export function ReHubEmptyState({ icon, title, body, action }: { icon: React.ReactNode; title: string; body: string; action?: React.ReactNode }) {
  const labelId = useId();
  return (
    <div className="rounded-2xl border border-dashed border-white/10 bg-[#101622]/60 px-6 py-16 text-center" role="status" aria-labelledby={labelId}>
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/5 text-slate-400">{icon}</div>
      <h2 id={labelId} className="text-base font-medium">{title}</h2>
      <p className="mx-auto mt-1 max-w-sm text-sm text-slate-400">{body}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ReHubLoadingSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading photos</span>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="overflow-hidden rounded-2xl border border-white/10 bg-[#101622]">
          <div className="aspect-[4/3] animate-pulse bg-white/5" />
          <div className="space-y-2 p-3">
            <div className="h-3 w-2/3 animate-pulse rounded bg-white/10" />
            <div className="h-2.5 w-1/2 animate-pulse rounded bg-white/5" />
          </div>
        </div>
      ))}
    </div>
  );
}
